import React, { useEffect, useState, useRef } from "react";
import { ENGINE_CHART_LIST } from "./engineList";
import { ENGINE_CHART_TYPE, ENGINE_TYPE } from "../../../constants/trendGraph/enginesLine";
import { Paper, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2"
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { getDataDuration, getSepDataDuration } from "../../../model/slice/dataDurationSlice";
import { Chart } from "../../chart/chart.jsx";
import { useVesselSettings } from "../../../helpers/vesselSetting/useVesselSettings";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType";
import { CHANNEL_TYPE, isDigitalChannel } from "../../../constants/channel/config";
import { NAVIGATION_BAR_HEIGHT } from "../../../constants/layout.js";
 
const useStyles = (theme) => ({
  paperHeight: {
    backgroundColor: "inherit",
    boxShadow: "none",
  },
  drawerButton: {
    position: "absolute",
    right: "1px",
    minWidth: "24px",
    width: "24px",
    color: "white",
  },
  baseGridContainer: {
    marginTop: "8px",
    width: "100% !important",
  },
  maximize: {
    position: "absolute",
    padding: "0px",
    [theme.breakpoints.down('lg')]: {},
  },
  titlePaperPosition: {
    position: "relative",
  },
  chart: {
 
  },
  controlContainer: {
    position: "absolute",
    right: "55px",
    [theme.breakpoints.down('lg')]: {
      right: "80px",
    },
  },
  titleNameHolder: {
    "userSelect": "none",
    "MozUserSelect": "none",
    "WebkitUserSelect": "none",
  },
});
 
export const EngineTrendBase = ({
  selectedEngine,
  values,
  isNoData,
  vesselId,
  vessel,
  graphType,
  isLoading,
  selectedChart,
  graphData,
  setGraphData,
  customChartLines,
  customLineData,
  isVesselOverview,
  setChartRef,
  overviewTrendIdx,
  isCustomCharts,
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const dataDurationState = useSelector(
    graphType === GRAPH_TYPE.ENGINE ? getDataDuration : getSepDataDuration
  );
  const [isGraphDataLoading, setIsGraphDataLoading] = useState(false);
  const [isFilteredNoData, setIsFilteredNoData] = useState(false);
  const [graphDataAnalog, setGraphDataAnalog] = useState([]);
  const [graphDataDigital, setGraphDataDigital] = useState([]);
 
  const { vesselSettings } = useVesselSettings({ vesselId });
  const screenshotRef = useRef(null);
 
  const digitalKeys = customChartLines?.filter(l => isDigitalChannel(l.channelType)).map(l => l.key);
 
  const generateDisplay = async () => {
    switch (graphType) {
      case GRAPH_TYPE.ENGINE: {
        if (
          values.mainEngineData === undefined ||
          values.engineData === undefined ||
          !values.mainEngineData?.length ||
          !values.engineData?.length
        ) {
          if (isLoading === false && isNoData === true) {
            setGraphData([]);
            setGraphDataAnalog([]);
          }
          return; //If either one of API values is missing, do nothing
        }
 
        const chartDetails = ENGINE_CHART_LIST.find(
          (e) => e.id === selectedChart && e.position === selectedEngine
        );
        let dataList = chartDetails?.dataList;
        if (vesselSettings.MEEngineType === ENGINE_TYPE.STRAIGHT) {
          dataList = dataList.filter((c) => !c.keyName.startsWith("meExhGasB"));
        }
        if (
          selectedChart === ENGINE_CHART_TYPE.ENGINE_DEVIATION ||
          selectedChart === ENGINE_CHART_TYPE.ENGINE_EXHAUST
        ) {
          dataList = dataList.filter(
            (b) =>
              !b.keyName.startsWith("meExhGas") ||
              b.keyName.match(/\d+/)?.[0] <= vesselSettings.cylinder
          );
        } else if (selectedChart === ENGINE_CHART_TYPE.ENGINE_TEMP) {
          dataList = dataList.filter(
            (b) =>
              b.keyName.match(/\d+/)?.[0] === undefined ||
              b.keyName.match(/\d+/)?.[0] <= vesselSettings.cylinder
          );
        }
 
        const displayData = await chartDetails?.reducer(
          values.mainEngineData,
          values.engineData,
          values.gpvtgData,
          dataDurationState.startDate,
          dataDurationState.endDate,
          dataList.map((d) => d.keyName)
        );
        setIsFilteredNoData(displayData?.length === 0)
        setGraphData(displayData);
        setGraphDataAnalog(displayData);
        break;
      }
      case GRAPH_TYPE.SEP:
        setGraphData(values);
        setGraphDataAnalog(values);
        break;
      case GRAPH_TYPE.CUSTOM: {
        let defaultAnalogChannels;
        let defaultDigitalChannels;
        if (customChartLines) {
          const analogChannels = customChartLines.filter(c => c.channelType === CHANNEL_TYPE.ANALOG).map(c => ({ [c.key]: undefined }));
          if (analogChannels?.length)
            defaultAnalogChannels = Object.assign(...analogChannels);

          const digitalChannels = customChartLines.filter(c => c.channelType === CHANNEL_TYPE.DIGITAL).map(c => ({ [c.key]: undefined }));
          if(digitalChannels?.length)
            defaultDigitalChannels = Object.assign(...digitalChannels);
        }
        const data = [];
        const analogData = [];
        const digitalData = [];
        values?.data?.forEach((d) => {
          let line = {
            dateTime: d.dateTime,
          };
          let lineAnalog = {
            dateTime: d.dateTime,
          };
          let lineDigital = {
            dateTime: d.dateTime,
          };
 
          Object.keys(d).forEach(k => {
            if (k != "dateTime") {
              if (digitalKeys?.includes(k)) {
                lineDigital = { ...lineDigital, [k]: d[k].status };
                line = { ...line, [k]: d[k].status };
              }
              else {
                lineAnalog = { ...lineAnalog, [k]: d[k].value };
                line = { ...line, [k]: d[k].value };
              }
            }
          });
          data.push(line);
          analogData.push({ ...defaultAnalogChannels, ...lineAnalog });
          digitalData.push({ ...defaultDigitalChannels, ...lineDigital });
        });
        setGraphData([...data]);
        setGraphDataAnalog([...analogData]);
        setGraphDataDigital([...digitalData]);
        break;
      }
    }
  };
 
  useEffect(() => {
    setIsGraphDataLoading(false);
  }, [graphDataAnalog, graphDataDigital]);
 
  useEffect(() => {
    if (isNoData || isLoading || !vesselSettings || Object.keys(vesselSettings).length === 0) return;
    setIsGraphDataLoading(true);
    generateDisplay();
  }, [values, selectedChart, isLoading, isNoData, vesselSettings, customChartLines]);
 
  useEffect(() => {
    //If data duration is changed, reset display data to new duration range
    setGraphData([]);
    setGraphDataDigital([]);
  }, [dataDurationState, vesselId]);
 
  return (
    <Grid container spacing={2} sx={styles.baseGridContainer}>
      <Grid item size={{xs: 12}} id="trendGraph" sx={styles.chart} ref={screenshotRef}>
        <Paper sx={{...styles.paperHeight, height: `calc(100dvh - ${NAVIGATION_BAR_HEIGHT}px - 230px - ${isCustomCharts ? 50 : 0}px)`}}>
          <Chart
            enginePosition={selectedEngine}
            chartType={selectedChart}
            graphData={graphData}
            graphDataDigital={graphDataDigital}
            customChartLines={customChartLines}
            isNoData={isNoData || isFilteredNoData}
            vesselId={vesselId}
            vessel={vessel}
            type={graphType}
            isInitialLoad={isLoading}
            isGraphDataLoading={isGraphDataLoading}
            customLineData={customLineData}
            isVesselOverview={isVesselOverview}
            setChartRef={setChartRef}
            overviewTrendIdx={overviewTrendIdx}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};
 
EngineTrendBase.propTypes = {
  selectedEngine: PropTypes.string,
  values: PropTypes.object,
  isDurationDisabled: PropTypes.bool.isRequired,
  isNoData: PropTypes.bool,
  isDisconnected: PropTypes.bool,
  vesselId: PropTypes.string,
  vessel: PropTypes.object,
  graphType: PropTypes.string,
  isLoading: PropTypes.bool,
  selectedChart: PropTypes.string,
  graphData: PropTypes.array,
  setGraphData: PropTypes.func,
  customChartLines: PropTypes.array,
  customLineData: PropTypes.object,
  isVesselOverview: PropTypes.bool,
  setChartRef: PropTypes.func,
  overviewTrendIdx: PropTypes.number,
  isCustomCharts: PropTypes.bool,
};